import DOMPurify from "dompurify";
import MarkdownIt from "markdown-it";
import { full as markdownItEmojiFull } from "markdown-it-emoji";
import { type FC, useMemo } from "react";

/**
 * Map of emoji shortcode name (without colons) to an inline SVG string.
 * Example: { phone: '<svg ...>...</svg>', pushpin: '<svg ...>...</svg>' }
 * Any shortcode not present here falls back to the unicode emoji from markdown-it-emoji.
 */
export type IconMap = Record<string, string>;

interface ExclusionNoteProps {
  exclusionInd: boolean;
  exclusionNote: string | null | undefined;
  /**
   * Optional custom SVG icons keyed by emoji shortcode name (e.g. "phone", "pushpin").
   * When provided, matching shortcodes render the supplied SVG instead of the unicode emoji.
   */
  iconMap?: IconMap;
}

function buildMarkdownIt(iconMap: IconMap = {}): MarkdownIt {
  const md = new MarkdownIt({ linkify: true, breaks: true }).use(markdownItEmojiFull);

  md.renderer.rules.emoji = (tokens, idx) => {
    const token = tokens[idx];
    const name = token.markup; // e.g. "phone", "pushpin"
    const customSvg = iconMap[name];

    if (customSvg) {
      // Sanitize the SVG before injecting it so callers can't introduce XSS via iconMap
      const safeSvg = DOMPurify.sanitize(customSvg, {
        USE_PROFILES: { svg: true, svgFilters: true },
      });
      return `<span class="exclusion-note-icon" role="img" aria-label="${name}">${safeSvg}</span>`;
    }

    // Fallback: unicode emoji character supplied by markdown-it-emoji
    return `<span role="img" aria-label="${name}">${token.content}</span>`;
  };

  return md;
}

export const ExclusionNote: FC<ExclusionNoteProps> = ({ exclusionInd, exclusionNote, iconMap }) => {
  // Must be called before any early return to satisfy Rules of Hooks
  const md = useMemo(() => {
    return buildMarkdownIt(iconMap);
  }, [iconMap]);

  if (!exclusionInd || !exclusionNote) return null;

  const rawHtml = md.render(exclusionNote);
  const safeHtml = DOMPurify.sanitize(rawHtml, {
    USE_PROFILES: { html: true },
  });

  return <div dangerouslySetInnerHTML={{ __html: safeHtml }} />;
};
